import * as React from "react";
import {FormEvent} from "react";

import {Link} from 'react-router-dom';
import Button from "@components/common/button/Button";
import {withStyles, WithStyles} from '@material-ui/core/styles';
import TranslateNew from "@translate/TranslateNew";
import Input from "@components/common/input/input.component";
import {HideShowPassword} from "@images/HideShowPassword";
import {LoginStyle} from "@components/login/login.style";
import {IconButton, Typography} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CornerRightElements2 from "@components/svg/cornerRightElements2";
import SelectLanguage from "@components/common/language/SelectLanguage";
import {getSavedPassword, isPasswordManagerAvailable} from '@helpers/passwordManager';

const tn: TranslateNew = TranslateNew.getInstance();

interface ILoginProps extends WithStyles<typeof LoginStyle> {
    onSubmit: (values: any, saveCredentials?: boolean) => void;
}

interface IState {
    passwordManagerAvailable: boolean;
    requestingPassword: boolean;
    passwordManagerError?: string;
    showPassword: boolean;
    formValues: {
        email: string,
        password: string,
    };
    formErrors: {
        email?: string | undefined,
        password?: string | undefined,
    };
}

export class Login extends React.Component<ILoginProps, IState> {
    private mounted = false;
    private availabilityRequest = 0;
    private usingSavedPassword = false;
    constructor(props: any) {
        super(props);

        this.state = {
            passwordManagerAvailable: false, requestingPassword: false,
            showPassword: false, formValues: {
                email: "", password: "",
            }, formErrors: {
                email: undefined, password: undefined,
            },
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleInputBlur = this.handleInputBlur.bind(this);
        this.validateEmail = this.validateEmail.bind(this);
        this.validatePassword = this.validatePassword.bind(this);
    }

    public componentDidMount() {
        this.mounted = true;
        this.refreshPasswordAvailability();
        document.addEventListener('resume', this.refreshPasswordAvailability);
    }

    public componentWillUnmount() {
        this.mounted = false;
        document.removeEventListener('resume', this.refreshPasswordAvailability);
    }

    public render() {
        const {formErrors, formValues} = this.state;
        const style = this.props.classes;

        return (<div className={style.root}>
            <div className={style.background}>
                <div className={style.circle}/>
                <CornerRightElements2 viewBox="-40 10 219 212" className={style.backgroundImgCornerTopRight}/>
            </div>
            <div className={style.languageSelect}>
                <SelectLanguage/>
            </div>
            <div className={style.container}>
                <div className={style.topBar}>
                    <Link to='/' style={{textDecoration: 'none'}}>
                        <Button variant="text" className={style.backButton}>
                            <ArrowBackIcon fontSize="inherit"/>
                            <span>{tn.lang.back}</span>
                        </Button>
                    </Link>
                </div>
                <div className={style.centerContent}>
                    <div className={style.topContent}>
                        <Typography variant="h2">{tn.lang.logInTitle}</Typography>
                    </div>
                    <form className={style.form} autoComplete="on" onSubmit={this.handleSubmit}>
                        <div className={style.formMiddleContent}>
                            <Input
                                type="email"
                                name="email"
                                id="email"
                                label={tn.lang.labelEmail}
                                autoComplete="username"
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                value={formValues.email}
                                onChange={this.handleInputChange}
                                onBlur={this.handleInputBlur}
                                error={formErrors.email}
                            />
                            <Input
                                name="password"
                                id="password"
                                type={this.state.showPassword ? "text" : "password"}
                                autoComplete="current-password"
                                label={tn.lang.labelPassword}
                                value={formValues.password}
                                onChange={this.handleInputChange}
                                onBlur={this.handleInputBlur}
                                error={formErrors.password}
                            >
                                <IconButton size="small"
                                            onClick={() => this.setState({showPassword: !this.state.showPassword})}>
                                    <HideShowPassword fill={this.state.showPassword ? '#9A8C9A' : '#242424'}/>
                                </IconButton>
                            </Input>
                        </div>
                        <div className={style.formBottomContent}>
                            {this.state.passwordManagerAvailable && (
                                <Button type="button" variant="text" fullWidth={true}
                                    disabled={this.state.requestingPassword} onClick={this.handleSavedPassword}>
                                    {tn.lang.useSavedPassword}
                                </Button>
                            )}
                            {this.state.passwordManagerError && (
                                <Typography role="status" variant="body2">{this.state.passwordManagerError}</Typography>
                            )}
                            <Button
                                type="submit"
                                variant='contained'
                                color='primary'
                                size="large"
                                fullWidth={true}
                            >
                                {tn.lang.logInButton}
                            </Button>
                            <Link to={'/forgot-password'} className={style.forgotPasswordLink}>
                                <Typography variant="body2"> {tn.lang.logInForgot}</Typography>
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>);
    }

    private refreshPasswordAvailability = () => {
        const request = ++this.availabilityRequest;
        isPasswordManagerAvailable().then(passwordManagerAvailable => {
            if (this.mounted && request === this.availabilityRequest) {
                this.setState({passwordManagerAvailable});
            }
        });
    }

    private validateEmail = (): boolean => {
        const {formValues} = this.state;
        if (!formValues.email) {
            this.setState((prevState) => ({
                formErrors: {
                    ...prevState.formErrors, email: tn.lang.validationEmailRequired,
                },
            }));
            return false;
        } else if (!tn.testEmailIsValid(formValues.email)) {
            this.setState((prevState) => ({
                formErrors: {
                    ...prevState.formErrors, email: tn.lang.validationEmailNotValid,
                },
            }));
            return false;
        }

        this.setState((prevState) => ({
            formErrors: {
                ...prevState.formErrors, email: undefined,
            },
        }));
        return true;
    }

    private validatePassword = (): boolean => {
        const {formValues} = this.state;

        if (!formValues.password) {
            this.setState((prevState) => ({
                formErrors: {
                    ...prevState.formErrors, password: tn.lang.validationPasswordRequired,
                },
            }));
            return false;
        }

        this.setState((prevState) => ({
            formErrors: {
                ...prevState.formErrors, password: undefined,
            },
        }));
        return true;
    }

    private handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        // Some WebView/password-manager combinations fill the DOM without an
        // input event. Read the submitted fields before validating React state.
        const email = event.currentTarget.elements.namedItem('email') as HTMLInputElement;
        const password = event.currentTarget.elements.namedItem('password') as HTMLInputElement;
        this.setState({formValues: {email: email.value, password: password.value}}, this.submitForm);
    }

    private submitForm = () => {
        const isEmailValid = this.validateEmail();
        const isPasswordValid = this.validatePassword();

        if (isEmailValid && isPasswordValid) {
            // Form submission logic here
            this.props.onSubmit(this.state.formValues, !this.usingSavedPassword);
        }
    }

    private handleSavedPassword = async () => {
        this.setState({requestingPassword: true, passwordManagerError: undefined});
        try {
            const credentials = await getSavedPassword();
            if (this.mounted && credentials) {
                this.usingSavedPassword = true;
                this.setState({formValues: credentials, formErrors: {}, showPassword: false});
            }
        } catch (_) {
            if (this.mounted) { this.setState({passwordManagerError: tn.lang.savedPasswordUnavailable}); }
        } finally {
            if (this.mounted) { this.setState({requestingPassword: false}); }
        }
    }

    private handleInputBlur = (event: React.FormEvent<HTMLInputElement>) => {
        const {name} = event.currentTarget;

        switch (name) {
            case 'email':
                this.validateEmail();
                break;
            case 'password':
                this.validatePassword();
                break;
        }
    }

    // Update form values in state when inputs change
    private handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.usingSavedPassword = false;
        const {name, value} = event.target;
        this.setState((prevState) => ({
            formValues: {
                ...prevState.formValues,
                [name]: value,
            },
            formErrors: {
                ...prevState.formErrors,
                [name]: undefined,
            },
        }));
    }
}

export default withStyles(LoginStyle)(Login);



// WEBPACK FOOTER //
// ./src/components/login/login.component.tsx